'use client';

import { useCallback, useRef } from 'react';
import { useLayoutSafe } from '@/hooks/useLayoutSafe';

/**
 * A slot whose content can appear late without moving the reader.
 *
 * The personal rails are ~1,900 px of content that only the client can decide on,
 * inserted immediately below the first rail. For a reader who has scrolled to the
 * feed before the fetches land that is worth **0.845 CLS** at 1280 (0.873 on a
 * throttled connection with a real scroll) — while every previous measurement of
 * this page read 0.000, because they were all taken parked at the top. See
 * `lib/stableInsert` for the full table and for the two fixes that measured worse.
 *
 * The children still mount and fetch immediately — they are only kept out of the
 * layout, so nothing is delayed except the moment they take up space. The anchor
 * div stays in flow at zero height so its position is measurable while its content
 * is not yet showing. (W14 R140)
 */
export default function StableInsert({ children }: { children: React.ReactNode }) {
  const anchor = useRef<HTMLDivElement>(null);
  const getTop = useCallback(() => anchor.current?.getBoundingClientRect().top ?? null, []);
  const safe = useLayoutSafe(getTop);

  return (
    <div ref={anchor} data-stable-insert>
      <div className={safe ? 'divide-y divide-hairline empty:hidden' : 'hidden'}>{children}</div>
    </div>
  );
}
